import React, { useMemo, useState, useEffect } from 'react';
import { MDBCol, MDBRow } from 'mdbreact';
import { post } from 'axios';
import PropTypes from 'prop-types';

import { pdiIcons } from 'pages/PDI/utils';

import PageStructure from 'components/PageStructure';
import FixedGrid from 'components/FixedGrid';

import { renderToast } from 'components/Informations/Toast/utils';
import ContentLoader from 'components/Informations/Loaders/ContentLoader';

import FilledButton from 'components/Buttons/FilledButton';
import InputField from 'components/FormGroup/InputField';
import ProgressBar from 'components/ProgressBar';
import RadioButton from 'components/FormGroup/RadioButton';

import AreaAccordion from 'pages/PDI/InternalComponents/AreaAccordion';

import QuestionBlock from 'pages/PDI/InternalComponents/Questions/QuestionBlock';
import QuestionContainer from 'pages/PDI/InternalComponents/Questions/QuestionContainer';
import QuestionItem from 'pages/PDI/InternalComponents/Questions/QuestionItem';

import Certified from 'pages/PDI/InternalComponents/InformationBlocks/Certified';
import KnowlageLevels from 'pages/PDI/InternalComponents/InformationBlocks/KnowledgeLevels';
import AcessingPerson from 'pages/PDI/InternalComponents/InformationBlocks/AcessingPerson';
import SendedCertificates from 'pages/PDI/InternalComponents/SendedCertificates';

import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';

import { arabicToRoman } from 'utils/numberManipulations';
import * as S from './styles';
import * as utils from './utils';

const icons = [
  { svg: pdiIcons('noKnowledge'), text: 'Nenhum' },
  { svg: pdiIcons('theoricalKnowledge'), text: 'Teórico' },
  { svg: pdiIcons('pontualKnowledge'), text: 'Pontual' },
  { svg: pdiIcons('dailyKnowledge'), text: 'Cotidiano' },
  { svg: pdiIcons('domainKnowledge'), text: 'Domínio' },
  { svg: pdiIcons('referenceKnowledge'), text: 'Referência' },
];

const { saveEvaluation, getEmployeesToSelect, getPDICertificates } = endpoints;

export default function EvaluationForm({ location, history /* user */ }) {
  // const { id_funcionario: userID } = user;
  const { state } = location;
  const { context } = state;

  const pageProps = utils.PageProps[context];

  const [answersSchema, setAnswersSchema] = useState({});
  const [blocksSchema, setBlocksSchema] = useState([]);

  const [langBlockHeader, setLangBlockHeader] = useState();
  const [activedArea, setActivedArea] = useState(NaN);

  const [sendData, setSendData] = useState([]);
  const [evaluationInfos, setEvaluationInfos] = useState({});
  const [evaluatedPersonInfos, setEvaluatedPersonInfos] = useState({
    name: '',
    function: '',
    seniorLevel: '',
    admissionDate: '',
  });

  const [loading, setLoading] = useState(true);
  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    closeTime: '',
  });

  const [certificates, setCerticates] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState();
  const [totalAnswers, setTotalAnswres] = useState();

  const url = utils.endpoints[context];

  const currentFluxURL =
    context === 'team-evaluation'
      ? `${url}/${state?.data?.idConfig}`
      : context === 'self-evaluation'
      ? `${url}/${state?.data?.idFuncionario}/0`
      : context === 'evaluation'
      ? `${url}/${state?.data?.idAvaliador}/${state?.data?.idAvaliacao}/0/0/0`
      : `${url}/${state?.data?.idAvaliacao}/${state?.data?.idMediador}/0/0`;

  const [PDIEvaluationData] = useFetch(currentFluxURL);
  const [allEmployees] = useFetch(getEmployeesToSelect);
  const [allCertificates] = useFetch(
    context === 'evaluation'
      ? `${getPDICertificates}/${state?.data?.idFuncionario}/${state?.data?.idConfig}`
      : '',
  );

  useEffect(() => {
    (async () => {
      const employees = await allEmployees;

      const {
        id_avaliacao: idAvaliacao,
        id_funcionario_avaliado: idFuncionario,
        descricao_tipo_avaliacao: descricao,
        num_certificados: qtdCertificados,
        respostas,
      } = await PDIEvaluationData;

      const currentEvaluated = await employees?.filter(employee => {
        const id = employee.id_funcionario;

        const { nome, nome_funcao: funcao, senioridade, step } = employee;

        if (id === idFuncionario) {
          setEvaluatedPersonInfos({
            nome,
            funcao,
            senioridade,
            step,
          });
        }
      });

      const result = await utils.generateStates(respostas);
      const resultBlocks = await utils.stateHeaderBlocks(respostas);
      const softSkillsOptions = await utils.getLengageHeaderOptions(respostas);
      const total = await Object.keys(result);

      setTotalQuestions(total);
      setBlocksSchema(resultBlocks);
      setAnswersSchema(result);
      setLangBlockHeader(softSkillsOptions);
      setCerticates(qtdCertificados || 0);

      setEvaluationInfos({
        id_avaliacao: idAvaliacao,
        id_funcionario: idFuncionario,
        descricao_tipo_avaliacao: descricao,
      });

      return respostas && result && resultBlocks && total && currentEvaluated
        ? setLoading(false)
        : setLoading(true);
    })();
  }, [PDIEvaluationData, allEmployees]);

  useEffect(() => {
    (async () => {
      const answers = await answersSchema;
      const { respostas } = (await PDIEvaluationData) || {};
      const tQuest = Object.entries(answers);

      const repliedQuestions = tQuest.filter(
        arr =>
          arr[1].answer !== null ||
          (!!arr[1]?.text && !!arr[1]?.text.length) ||
          (!!arr[1]?.all_answer && !!arr[1]?.all_answer.length),
      );

      const collectAnswres = repliedQuestions.flatMap(i => i[0]);
      const data = utils.MountAnswers(answers, respostas, context);

      setTotalAnswres(collectAnswres);
      setSendData(data);
    })();
  }, [answersSchema]);

  const handleSelectAll = (iArea, iBloco) => e => {
    e.persist();
    const { value } = e.target;
    const allAnswer = parseInt(value);

    setAnswersSchema(prev =>
      utils.replyAllBlockQuest(
        prev,
        `${iArea}-${iBloco}-`,
        'answer',
        allAnswer,
        'questions',
      ),
    );
    setBlocksSchema(prev =>
      utils.replyAllBlockQuest(
        prev,
        `${iArea}-${iBloco}`,
        'all_selected',
        allAnswer,
        'header',
      ),
    );
  };

  const handleCheckAll = (iArea, iBloco) => e => {
    e.persist();
    const { checked } = e.target;

    setAnswersSchema(prev =>
      utils.replyAllBlockQuest(
        prev,
        `${iArea}-${iBloco}`,
        'certified',
        checked,
        'questions',
      ),
    );
    setBlocksSchema(prev =>
      utils.replyAllBlockQuest(
        prev,
        `${iArea}-${iBloco}`,
        'all_certified',
        checked,
        'header',
      ),
    );
  };

  const handleCheckEach = (iArea, iBloco, iQuest) => e => {
    e.persist();
    const { checked } = e.target;

    setAnswersSchema(prev => {
      prev[`${iArea}-${iBloco}-${iQuest}`].certified = checked;
      return { ...prev };
    });
    setBlocksSchema(prev =>
      utils.replyAllBlockQuest(
        prev,
        `${iArea}-${iBloco}`,
        'all_certified',
        false,
      ),
    );
  };

  const handleSetAlternative = (iArea, iBloco, iQuest) => e => {
    e.persist();
    const { value } = e.target;
    const answer = parseInt(value);

    setAnswersSchema(prev => {
      prev[`${iArea}-${iBloco}-${iQuest}`].answer = answer;
      return { ...prev };
    });
    setBlocksSchema(prev =>
      utils.replyAllBlockQuest(
        prev,
        `${iArea}-${iBloco}`,
        'all_selected',
        null,
      ),
    );
    e.preventDefault();
  };

  const handleSelectAlternative = (iArea, iBloco, iQuest) => e => {
    e.persist();
    const { value } = e.target;

    setAnswersSchema(prev => {
      const arrValues = prev[`${iArea}-${iBloco}-${iQuest}`].all_answer;
      const findVal = arrValues?.find(item => item === value)?.length;
      const newArrValues = !findVal
        ? arrValues
          ? [...arrValues, value]
          : [...new Set([value])]
        : arrValues?.filter(i => i !== value);
      prev[`${iArea}-${iBloco}-${iQuest}`].all_answer = newArrValues;
      return { ...prev };
    });
    e.preventDefault();
  };

  const handleInput = (iArea, iBloco, iQuest) => e => {
    e.persist();

    const { value } = e.target;

    setAnswersSchema(prev => {
      prev[`${iArea}-${iBloco}-${iQuest}`].text = value;
      return { ...prev };
    });
  };

  const handleActiveAccordeon = activedIndex => e => {
    e.persist();

    const { target, nativeEvent } = e;
    const { checked: actived } = target;
    const { scrollTo, outerHeight } = window;

    if (actived) {
      scrollTo(0, outerHeight - nativeEvent.clientY);
    }

    setActivedArea(activedIndex);
  };

  const RenderAlternativas = (iArea, iBloco, iQuest) => (alternativa, i) => (
    <S.RadioContainer>
      <RadioButton
        key={alternativa + i}
        ref={null}
        id={`choice-${iArea}-${iBloco}-${iQuest}-${alternativa}`}
        value={alternativa}
        checked={
          answersSchema[`${iArea}-${iBloco}-${iQuest}`]?.answer === alternativa
        }
        onChange={handleSetAlternative(iArea, iBloco, iQuest)}
        labelText="."
      />
    </S.RadioContainer>
  );

  const RenderCheckBoxes = (iArea, iBloco, iQuest) => (alternativa, i) => (
    <S.CaixaDeSelecao
      key={iQuest + i}
      ref={null}
      id={`cx-select-${iArea}-${iBloco}-${iQuest}-${i}`}
      labelText={alternativa}
      value={alternativa}
      // eslint-disable-next-line camelcase
      checked={answersSchema[`${iArea}-${iBloco}-${iQuest}`]?.all_answer?.find(
        opt => opt === alternativa,
      )}
      onChange={handleSelectAlternative(iArea, iBloco, iQuest)}
    />
  );

  // SOFT SKILLS OUTRAS QUESTOES
  const RenderOutrasAlternativas = (iArea, iBloco, iQuest, opts) => (
    alternativa,
    i,
  ) => (
    <S.RadioContainer>
      <RadioButton
        key={alternativa + i}
        hasLabel
        labelText={opts?.find((_, idx) => idx === i)}
        ref={null}
        id={`choice-${iArea}-${iBloco}-${iQuest}-${alternativa}`}
        value={alternativa}
        checked={
          answersSchema[`${iArea}-${iBloco}-${iQuest}`]?.answer === alternativa
        }
        onChange={handleSetAlternative(iArea, iBloco, iQuest)}
      />
    </S.RadioContainer>
  );

  // QUESTÕES DE HARD SKILL
  const RenderQuestoes = (iArea, iBloco) => (questao, iQuest) => (
    <QuestionItem key={iArea + iBloco + iQuest}>
      <div>{questao.nome}</div>
      {questao.alternativas?.map(RenderAlternativas(iArea, iBloco, iQuest))}
      <div>
        <S.CaixaDeSelecao
          id={`has-certified-${iArea}-${iBloco}-${iQuest}`}
          checked={answersSchema[`${iArea}-${iBloco}-${iQuest}`]?.certified}
          onChange={handleCheckEach(iArea, iBloco, iQuest)}
          labelText=""
        />
      </div>
    </QuestionItem>
  );

  // QUESTÕES DE SOFT SKILLS - IDIOMAS
  const ssLangRenderQuestoes = (iArea, iBloco) => (questao, iQuest) => {
    const len = questao.alternativas?.length;
    const regEx = utils.regExGen('outros');

    return (
      <QuestionItem blocks={len || 1} key={iArea + iBloco + iQuest}>
        <div>{questao.nome}</div>
        {questao.alternativas?.map(RenderAlternativas(iArea, iBloco, iQuest))}
        {questao.nome.search(regEx) > -1 && (
          <S.otherLangsContainer>
            <InputField
              value={answersSchema[`${iArea}-${iBloco}-${iQuest}`]?.text}
              labelText="Quais outros idiomas?"
              handleChange={handleInput(iArea, iBloco, iQuest)}
              name={`input-${iArea}-${iBloco}-${iQuest}`}
              placeholderText="Insira os idiomas"
            />
            <small>Separe os idiomas por vírgula (,)</small>
          </S.otherLangsContainer>
        )}
      </QuestionItem>
    );
  };

  // QUESTÕES DE  SOFT SKILS && DIRECIONAMENTO DE CARREIRA
  const OtherRenderQuestoes = (iArea, iBloco) => (questao, iQuest) => {
    // const id = questao.id_tipo_questao_template;
    const tipo = questao.descricao_tipo_questao;

    return tipo.match(utils.regExGen('Nível')) ? (
      <QuestionItem key={iArea + iBloco + iQuest}>
        <div>{questao.nome}</div>
        {questao.alternativas?.map(RenderAlternativas(iArea, iBloco, iQuest))}
        <div>
          <S.CaixaDeSelecao
            id={`has-certified-${iArea}-${iBloco}-${iQuest}`}
            checked={answersSchema[`${iArea}-${iBloco}-${iQuest}`]?.certified}
            onChange={handleCheckEach(iArea, iBloco, iQuest)}
            labelText=""
          />
        </div>
      </QuestionItem>
    ) : (
      <QuestionItem blocks={1} key={iArea + iBloco + iQuest}>
        <S.otherQuestionsContainer>
          <div>{questao.nome}</div>
          {tipo.match(utils.regExGen('Multipla')) ? (
            <div>
              {questao.alternativas?.map(
                RenderOutrasAlternativas(
                  iArea,
                  iBloco,
                  iQuest,
                  questao.opcoes_resposta,
                ),
              )}
            </div>
          ) : tipo.match(utils.regExGen('Caixas')) ? (
            <S.cbContainer>
              {questao.opcoes_resposta?.map(
                RenderCheckBoxes(iArea, iBloco, iQuest),
              )}
            </S.cbContainer>
          ) : (
            <div />
          )}
        </S.otherQuestionsContainer>
      </QuestionItem>
    );
  };

  // HEADER DOS BLOCOS DE HARD SKILL
  const RenderHeaderBlocos = (iArea, iBloco) => (icon, i) => {
    const num = i < 2 ? i : ++i;
    const resultValue = utils.fib(num);

    return (
      <S.headerDiv
        key={iArea + iBloco + i}
        title="Selecionar esta resposta às questões deste bloco"
      >
        {icon.svg}
        <RadioButton
          id={`selectAll-${iArea}-${iBloco}-${i}`}
          checked={
            blocksSchema &&
            blocksSchema[`${iArea}-${iBloco}`]?.all_selected === resultValue
          }
          value={resultValue}
          onChange={handleSelectAll(iArea, iBloco)}
        />
        <S.headerSmall>{icon.text}</S.headerSmall>
      </S.headerDiv>
    );
  };

  // HEADER DOS BLOCOS SOFT SKILLS
  const ssRenderHeaderBlocks = (iArea, iBloco) => (item, idx) => (
    <S.headerDivSS blocks={langBlockHeader.length} key={iArea + iBloco + idx}>
      <RadioButton
        id={`selectAll-${iArea}-${iBloco}-${idx}`}
        checked={
          blocksSchema &&
          blocksSchema[`${iArea}-${iBloco}`]?.all_selected === item.value
        }
        value={item.value}
        onChange={handleSelectAll(iArea, iBloco)}
      />
      <S.headerSmall>{item.label}</S.headerSmall>
    </S.headerDivSS>
  );

  // render dos blocos "filhos" de cada Area
  const RenderBlocos = (area, iArea) => (bloco, iBloco) => {
    const regEx = utils.regExGen('idiomas');
    const isLangBlock = bloco.nome?.search(regEx) > -1;

    return (
      !!bloco.questoes.length && (
        <>
          <QuestionBlock blockName={bloco.nome}>
            {isLangBlock ? (
              langBlockHeader.map(ssRenderHeaderBlocks(iArea, iBloco))
            ) : (
              <>
                {icons.map(RenderHeaderBlocos(iArea, iBloco))}
                <S.headerDiv>
                  {pdiIcons('certifiedKnowledge')}
                  <S.CaixaDeSelecao
                    id={`selectAll-${iArea}-${iBloco}`}
                    checked={
                      blocksSchema &&
                      blocksSchema[`${iArea}-${iBloco}`].all_certified
                    }
                    onChange={handleCheckAll(iArea, iBloco)}
                  />
                  <small>Certificado</small>
                </S.headerDiv>
              </>
            )}
          </QuestionBlock>
          <QuestionContainer key={bloco.id_bloco}>
            {bloco?.questoes.map(
              isLangBlock
                ? ssLangRenderQuestoes(iArea, iBloco)
                : RenderQuestoes(iArea, iBloco),
            )}
          </QuestionContainer>
        </>
      )
    );
  };

  // SOFT SKILLS
  const ssRenderBlocos = iArea => (bloco, iBloco) => {
    const regEx = utils.regExGen('idiomas');
    const isLangBlock = bloco.nome?.search(regEx) > -1;

    return (
      <>
        <QuestionBlock blockName={bloco.nome}>
          {isLangBlock ? (
            langBlockHeader.map(ssRenderHeaderBlocks(iArea, iBloco))
          ) : (
            <div />
          )}
        </QuestionBlock>
        <QuestionContainer>
          {bloco?.questoes.map(
            isLangBlock
              ? ssLangRenderQuestoes(iArea, iBloco)
              : OtherRenderQuestoes(iArea, iBloco),
          )}
        </QuestionContainer>
      </>
    );
  };

  // DIRECIONAMENTO DE CARREIRA
  const dcRenderBlocos = iArea => (bloco, iBloco) => (
    <>
      <QuestionBlock blockName={bloco.nome} />
      <QuestionContainer>
        {bloco.questoes.map(OtherRenderQuestoes(iArea, iBloco))}
      </QuestionContainer>
    </>
  );

  // render das Areas
  const renderAreas = () => (area, iArea) => (
    <AreaAccordion
      headTitle={area.nome || area.descricao}
      accordionId={Math.random() * area.id_area_template}
      accordionName="areas"
      key={area.id_area_template + iArea}
      tQuestions={utils.questionsPerArea(totalQuestions, iArea)}
      tAnswers={utils.questionsPerArea(totalAnswers, iArea)}
      handleActive={handleActiveAccordeon(area.id_area_template)}
      Actions={() => {}}
    >
      {activedArea === area.id_area_template &&
        area.blocos.map(
          area.softskills
            ? ssRenderBlocos(iArea, area?.blocos)
            : area.carreira
            ? dcRenderBlocos(iArea)
            : RenderBlocos(area, iArea),
        )}
    </AreaAccordion>
  );

  const values = useMemo(
    () => ({
      numQuestions: totalQuestions?.length,
      numAnswers: totalAnswers?.length,
    }),
    [totalQuestions, totalAnswers],
  );

  const handleSave = async isFinal => {
    setLoading(true);

    try {
      await post(saveEvaluation, {
        ...evaluationInfos,
        respostas: sendData,
      })
        .then(() => setLoading(false))
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'success',
            message: 'Avaliação salva com sucesso',
            closeTime: 4000,
          });
        })
        .then(() => (isFinal ? {} : history.push('/auth/pdi')));
    } catch {
      setLoading(false);
      setToastVisibility(true);

      return setToastInfos({
        type: 'error',
        message: 'Erro ao salvar a avaliação',
        closeTime: 6000,
      });
    }
  };

  const finalizeEvaluation = async () => {
    await handleSave(true);
    return history.push('/auth/pdi/evaluation/resume', { ...state });
  };

  return (
    <>
      {loading && <ContentLoader isFull />}

      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.closeTime,
          setToastVisibility,
        )}

      <PageStructure title={pageProps?.title} subtitle={pageProps?.subtitle}>
        <MDBRow>
          <MDBCol sm="12" md="9">
            {!!certificates?.length && context === 'evaluation' && (
              <S.CertificatesContainer>
                <SendedCertificates certificates={allCertificates || []} />
              </S.CertificatesContainer>
            )}
            {PDIEvaluationData?.respostas?.map(renderAreas())}
            <S.ButtonsContainer>
              <FilledButton
                handleClick={() => handleSave(true)}
                buttonType="button"
                buttonClass="cancel"
                text="Salvar e continuar depois"
              />
              <FilledButton
                handleClick={finalizeEvaluation}
                buttonType="button"
                text="Concluir avaliação"
                disabled={values.numAnswers < values.numQuestions}
                hasTooltip
                tooltipText="Responda todas as questões para concluir sua avaliação"
              />
            </S.ButtonsContainer>
          </MDBCol>
          <MDBCol sm="12" md="3">
            <FixedGrid>
              {(context === 'evaluation' ||
                context === 'team-evaluation' ||
                context === 'mediation') && (
                <AcessingPerson
                  personName={evaluatedPersonInfos?.nome}
                  personFunction={evaluatedPersonInfos?.funcao}
                  personSeniorLevel={`${
                    evaluatedPersonInfos?.senioridade
                  } ${arabicToRoman(evaluatedPersonInfos?.step)}`}
                  personAdmissionDate="16/06/2019"
                />
              )}
              <ProgressBar
                hasCounter
                totalValue={values.numQuestions}
                currentValue={values.numAnswers}
                headText="Questões respondidas"
              />
              <KnowlageLevels />
              <Certified />
            </FixedGrid>
          </MDBCol>
        </MDBRow>
      </PageStructure>
    </>
  );
}

EvaluationForm.defaultProps = {
  location: {},
  user: {},
  history: {},
};

EvaluationForm.propTypes = {
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ),
};
